import React, { useState, useRef } from 'react';
import { Plus, Edit2, Trash2, X, ShoppingBag, Upload, Image, FileText } from 'lucide-react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import CSVUpload from './CSVUpload';
import './ProductManagement.css';

function ProductManagement({ products, onAdd, onUpdate, onDelete }) {
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: 'Accessories',
    imageUrl: '',
    additionalImages: []
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [additionalPreviews, setAdditionalPreviews] = useState([]);
  const fileInputRef = useRef(null);
  const additionalFileInputRef = useRef(null);

  const handleCSVUploadSuccess = (uploadedProducts) => {
    console.log('CSV upload successful:', uploadedProducts);
    setShowCSVUpload(false);
    // Refresh products list by calling parent's refresh function
    if (onAdd) {
      uploadedProducts.forEach(product => {
        onAdd(product);
      });
    }
  };

  const categories = [
    { name: 'Accessories', color: '#14b8a6' },
    { name: 'Bags', color: '#a855f7' },
    { name: 'Flags', color: '#f43f5e' },
    { name: 'Hats', color: '#ef4444' },
    { name: 'Hoodies', color: '#3b82f6' },
    { name: 'Jackets', color: '#6366f1' },
    { name: 'Mugs', color: '#f59e0b' },
    { name: 'Skully', color: '#06b6d4' },
    { name: 'Sleeves', color: '#ec4899' },
    { name: 'Sweat Suits', color: '#f97316' },
    { name: 'Totes', color: '#8b5cf6' },
    { name: 'Towels', color: '#84cc16' },
    { name: 'T-Shirts', color: '#10b981' }
  ];

  const handleImageUpload = async (file) => {
    if (!file) return;

    setUploadingImage(true);
    try {
      const fileName = `products/${Date.now()}-${file.name}`;
      const storageRef = ref(storage, fileName);
      
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      
      setFormData({ ...formData, imageUrl: downloadUrl });
      setImagePreview(downloadUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size must be less than 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);

      handleImageUpload(file);
    }
  };

  const handleAdditionalImagesChange = async (e) => {
    const files = Array.from(e.target.files);
    const currentImages = formData.additionalImages || [];
    
    if (currentImages.length + files.length > 3) {
      alert('You can only add up to 3 additional images (4 total including main image)');
      return;
    }

    setUploadingImage(true);
    try {
      const uploadPromises = files.map(async (file) => {
        if (file.size > 5 * 1024 * 1024) {
          throw new Error('Image size must be less than 5MB');
        }
        
        if (!file.type.startsWith('image/')) {
          throw new Error('Please select image files only');
        }

        const fileName = `products/${Date.now()}-${file.name}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, file);
        return await getDownloadURL(storageRef);
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      const newAdditionalImages = [...currentImages, ...uploadedUrls];
      
      setFormData({ ...formData, additionalImages: newAdditionalImages });
      setAdditionalPreviews(newAdditionalImages);
    } catch (error) {
      console.error('Error uploading images:', error);
      alert(error.message || 'Error uploading images. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeAdditionalImage = (index) => {
    const newImages = formData.additionalImages.filter((_, i) => i !== index);
    setFormData({ ...formData, additionalImages: newImages });
    setAdditionalPreviews(newImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const productData = {
      ...formData,
      price: parseFloat(formData.price)
    };

    try {
      if (editingProduct) {
        await onUpdate(editingProduct.id, productData);
      } else {
        await onAdd(productData);
      }
      resetForm();
    } catch (error) {
      alert('Error saving product. Please try again.');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      category: product.category,
      imageUrl: product.imageUrl || '',
      additionalImages: product.additionalImages || []
    });
    setImagePreview(product.imageUrl || '');
    setAdditionalPreviews(product.additionalImages || []);
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await onDelete(productId);
      } catch (error) {
        alert('Error deleting product. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      category: 'Accessories',
      imageUrl: '',
      additionalImages: []
    });
    setImagePreview('');
    setAdditionalPreviews([]);
    setEditingProduct(null);
    setShowForm(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (additionalFileInputRef.current) {
      additionalFileInputRef.current.value = '';
    }
  };

  const filteredProducts = selectedCategory === 'All' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  const getCategoryColor = (categoryName) => {
    const category = categories.find(cat => cat.name === categoryName);
    return category ? category.color : '#6b7280';
  };

  return (
    <div className="product-management">
      <div className="products-header">
        <h2>Product Catalog</h2>
        <div className="header-buttons">
          <button className="csv-upload-btn" onClick={() => setShowCSVUpload(true)}>
            <FileText size={20} />
            Import CSV
          </button>
          <button className="add-product-btn" onClick={() => setShowForm(true)}>
            <Plus size={20} />
            Add Product
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        <div className="filter-options">
          <button 
            className={`filter-btn ${selectedCategory === 'All' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('All')}
          >
            All Products
            <span className="count">{products.length}</span>
          </button>
          {categories.map(category => {
            const count = products.filter(p => p.category === category.name).length;
            return (
              <button 
                key={category.name}
                className={`filter-btn ${selectedCategory === category.name ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.name)}
                style={{ '--category-color': category.color }}
              >
                {category.name}
                <span className="count">{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="products-grid">
        {filteredProducts.map(product => (
          <div key={product.id} className="admin-product-card">
            <div className="admin-product-image">
              <div className="admin-product-image-frame">
                {product.imageUrl ? (
                  <img src={product.imageUrl} alt={product.name} />
                ) : (
                  <div className="placeholder-image">
                    <ShoppingBag size={48} />
                    <span>No Image</span>
                  </div>
                )}
              </div>
            </div>

            <div className="admin-product-info">
              <div className="admin-product-main">
                <div className="product-category-badge" style={{ backgroundColor: getCategoryColor(product.category) }}>
                  {product.category}
                </div>
                <div className="product-price">${product.price.toFixed(2)}</div>
              </div>
              <div className="admin-product-details">
                <h3>{product.name}</h3>
                <p>{product.description}</p>
              </div>
            </div>

            {/* Admin Action Overlay - like customer view */}
            <div className="admin-action-overlay">
              <button 
                className="admin-edit-btn"
                onClick={() => handleEdit(product)}
                title="Edit product"
              >
                ✏️
              </button>
              <button 
                className="admin-delete-btn"
                onClick={() => handleDelete(product.id)}
                title="Delete product"
              >
                🗑️
              </button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="product-form-overlay" onClick={(e) => {
          if (e.target.className === 'product-form-overlay') {
            resetForm();
          }
        }}>
          <div className="product-form-panel">
            <div className="form-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
              <button className="close-btn" onClick={resetForm} type="button">
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="product-form">
              <div className="form-group">
                <label htmlFor="name">Product Name *</label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Classic Trinidad T-Shirt"
                />
              </div>

              <div className="form-group">
                <label htmlFor="category">Category *</label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  {categories.map(cat => (
                    <option key={cat.name} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="price">Price (USD) *</label>
                <input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  required
                  placeholder="29.99"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description *</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows="4"
                  placeholder="Describe your product..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="imageUrl">Product Image</label>
                <div className="image-upload-section">
                  <div className="image-upload-area">
                    {imagePreview ? (
                      <div className="image-preview-container">
                        <img src={imagePreview} alt="Product preview" className="image-preview" />
                        <button
                          type="button"
                          className="remove-image-btn"
                          onClick={() => {
                            setImagePreview('');
                            setFormData({ ...formData, imageUrl: '' });
                            if (fileInputRef.current) {
                              fileInputRef.current.value = '';
                            }
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <Upload size={48} />
                        <p>Click to upload image</p>
                        <p className="upload-hint">PNG, JPG up to 5MB</p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      id="imageUrl"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <button
                      type="button"
                      className="upload-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                    >
                      {uploadingImage ? 'Uploading...' : 'Choose Image'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Additional Images Section */}
              <div className="form-group">
                <label>Additional Images (Optional - Up to 3 more)</label>
                <div className="additional-images-section">
                  <div className="additional-images-grid">
                    {additionalPreviews.map((img, index) => (
                      <div key={index} className="additional-image-preview">
                        <img src={img} alt={`Additional ${index + 1}`} />
                        <button
                          type="button"
                          className="remove-additional-btn"
                          onClick={() => removeAdditionalImage(index)}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    {additionalPreviews.length < 3 && (
                      <div className="add-more-images">
                        <input
                          ref={additionalFileInputRef}
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleAdditionalImagesChange}
                          className="hidden"
                        />
                        <button
                          type="button"
                          className="add-images-btn"
                          onClick={() => additionalFileInputRef.current?.click()}
                          disabled={uploadingImage}
                        >
                          <Upload size={24} />
                          <span>Add Images</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <p className="upload-hint">Add up to 3 more images. Customers can swipe through them.</p>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={resetForm}>
                  Cancel
                </button>
                <button type="submit" className="save-btn">
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CSV Upload Modal */}
      {showCSVUpload && (
        <CSVUpload 
          onClose={() => setShowCSVUpload(false)}
          onUploadSuccess={handleCSVUploadSuccess}
        />
      )}
    </div>
  );
}

export default ProductManagement;
